import React from "react";
import "./Perspective.css";

import perspectiveImg from "../../images/perspectives.jpg";

type IconName =
  | "grid"
  | "globe"
  | "chip"
  | "idea";

interface PerspectiveItem {
  number: string;
  title: string;
  description: string;
  icon: IconName;
}

const perspectiveItems: PerspectiveItem[] = [
  {
    number: "01",
    title: "Semiconductor Industry Perspectives",
    description:
      "Insights into emerging trends, technology evolution, and industry developments.",
    icon: "globe",
  },
  {
    number: "02",
    title: "Engineering & Design",
    description:
      "Technical viewpoints on RTL design, verification, physical design, DFT, and silicon realization.",
    icon: "chip",
  },
  {
    number: "03",
    title: "Technology & Innovation",
    description:
      "Perspectives on AI, advanced nodes, chiplets, high-performance computing, automotive electronics, and next-generation semiconductor technologies.",
    icon: "idea",
  },
];

interface PerspectiveIconProps {
  name: IconName;
  size?: number;
}

const PerspectiveIcon: React.FC<PerspectiveIconProps> = ({
  name,
  size = 34,
}) => {
  const commonProps = {
    width: size,
    height: size,
    viewBox: "0 0 48 48",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: 1.7,
    strokeLinecap: "round" as const,
    strokeLinejoin: "round" as const,
    "aria-hidden": true,
  };

  const icons: Record<IconName, React.ReactNode> = {
    grid: (
      <>
        <rect x="7" y="7" width="9" height="9" />
        <rect x="20" y="7" width="9" height="9" />
        <rect x="33" y="7" width="8" height="9" />

        <rect x="7" y="20" width="9" height="9" />
        <rect x="20" y="20" width="9" height="9" />
        <rect x="33" y="20" width="8" height="9" />

        <rect x="7" y="33" width="9" height="8" />
        <rect x="20" y="33" width="9" height="8" />
        <rect x="33" y="33" width="8" height="8" />
      </>
    ),

    globe: (
      <>
        <circle cx="24" cy="24" r="18" />
        <path d="M6 24h36" />
        <path d="M24 6c6 5 9 11 9 18s-3 13-9 18" />
        <path d="M24 6c-6 5-9 11-9 18s3 13 9 18" />
        <path d="M10 14h28" />
        <path d="M10 34h28" />
      </>
    ),

    chip: (
      <>
        <rect x="12" y="12" width="24" height="24" rx="3" />
        <rect x="19" y="19" width="10" height="10" />

        <path d="M17 4v8" />
        <path d="M24 4v8" />
        <path d="M31 4v8" />

        <path d="M17 36v8" />
        <path d="M24 36v8" />
        <path d="M31 36v8" />

        <path d="M4 17h8" />
        <path d="M4 24h8" />
        <path d="M4 31h8" />

        <path d="M36 17h8" />
        <path d="M36 24h8" />
        <path d="M36 31h8" />
      </>
    ),

    idea: (
      <>
        <path d="M18 33h12" />
        <path d="M20 38h8" />
        <path d="M22 42h4" />
        <path d="M15 29c-3-3-5-7-5-12a14 14 0 1 1 28 0c0 5-2 9-5 12-2 2-3 4-3 7H18c0-3-1-5-3-7Z" />
      </>
    ),
  };

  return <svg {...commonProps}>{icons[name]}</svg>;
};

const Perspective: React.FC = () => {
  return (
    <section
      className="perspective-section pt-30"
      id="perspectives"
    >
      <div className="container-fluid">
        <div className="perspective-wrap sec-border mlr-20">

          {/* =====================================================
              HERO
          ===================================================== */}

          <div className="perspective-hero">

            {/* LEFT CONTENT */}
            <div className="perspective-hero__content">

              <h2 className="perspective-kicker">
                <PerspectiveIcon
                  name="grid"
                  size={17}
                />

                Perspectives
              </h2>

              <p className="perspective-hero__description">
                Engineering excellence extends beyond execution. It requires
                continuous learning, technical insight, and a deep understanding
                of the evolving semiconductor landscape. Through Perspectives,
                Digicomm shares engineering viewpoints, technology trends,
                technical expertise, and practical knowledge that contribute to
                informed decision-making across the semiconductor industry.
              </p>

            </div>

            {/* RIGHT IMAGE HOLDER */}
            <div className="perspective-hero__visual">

              <div className="perspective-hero__image">
                <img
                  src={perspectiveImg}
                  alt="Semiconductor engineering perspectives and technology insights"
                />
              </div>

            </div>

          </div>

          {/* =====================================================
              FEATURED PERSPECTIVES
          ===================================================== */}

          <div className="perspective-section-heading">

            <span />

            <h3>
              Featured Perspectives
              <small>
                (Illustrative Categories)
              </small>
            </h3>

            <span />

          </div>

          {/* =====================================================
              CARDS
          ===================================================== */}

          <div className="perspective-cards">

            {perspectiveItems.map((item) => (
              <article
                className="perspective-card"
                key={item.number}
              >

                <div className="perspective-card__header">

                  <span className="perspective-card__number">
                    {item.number}
                  </span>

                  <span className="perspective-card__icon">
                    <PerspectiveIcon
                      name={item.icon}
                      size={38}
                    />
                  </span>

                </div>

                <h3>
                  {item.title}
                </h3>

                <span className="perspective-card__line" />

                <p>
                  {item.description}
                </p>

              </article>
            ))}

          </div>

        </div>
      </div>
    </section>
  );
};

export default Perspective;